import React, { useState, useEffect, useMemo } from 'react';
import { parseGedcomToTree } from '../utils/gedcomParser';
import { 
  getAllEvents, 
  getDaysUntil, 
  formatCountdown, 
  getUpcomingEvents, 
  getTodayEvents, 
  getThisMonthEvents,
  getMilestoneEvents,
  getOnThisDayEvents,
  generateCalendarStats,
  formatDisplayDate,
  getOrdinalSuffix,
  downloadICalFile
} from '../utils/calendarUtils';
import ExportShareControls from '../components/ExportShareControls';
import '../components/EnhancedComponents.css';

const CalendarPage = () => {
  const [gedcomData, setGedcomData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Calendar view states - all integrated on one page
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard', 'month', 'year', 'timeline'
  const [selectedEventType, setSelectedEventType] = useState('all');

  useEffect(() => {
    const loadGedcomData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch('/data/rossetti.ged');
        
        if (response.ok) {
          const content = await response.text();
          const parsed = parseGedcomToTree(content);
          setGedcomData(parsed);
        } else {
          throw new Error(`Failed to fetch GEDCOM file: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Calendar: Error loading GEDCOM data:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadGedcomData();
  }, []);

  // Extract all events using the consolidated utility
  const allEvents = useMemo(() => {
    return gedcomData ? getAllEvents(gedcomData) : [];
  }, [gedcomData]);

  // Separate original events from anniversaries
  const originalEvents = useMemo(() => {
    return allEvents.filter(event => !event.type.includes('anniversary'));
  }, [allEvents]);

  const anniversaries = useMemo(() => {
    return allEvents.filter(event => event.type.includes('anniversary'));
  }, [allEvents]);

  // Filter events based on selected type
  const filteredEvents = useMemo(() => {
    if (selectedEventType === 'all') return allEvents;
    if (selectedEventType === 'anniversaries') return anniversaries;
    if (selectedEventType === 'historical') return originalEvents;
    return allEvents.filter(event => 
      event.type === selectedEventType || event.type === `${selectedEventType}-anniversary`
    );
  }, [allEvents, anniversaries, originalEvents, selectedEventType]);

  // Navigation functions
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateYear = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(newDate.getFullYear() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Dashboard data
  const upcomingEvents = getUpcomingEvents(allEvents, 30);
  const todayEvents = getTodayEvents(allEvents);
  const thisMonthEvents = getThisMonthEvents(allEvents);
  const milestoneEvents = getMilestoneEvents(allEvents);
  const onThisDayEvents = getOnThisDayEvents(allEvents);
  const stats = generateCalendarStats(allEvents, gedcomData);

  // Export handlers
  const handleExportUpcoming = () => {
    downloadICalFile(upcomingEvents, 'upcoming-family-events.ics', 'Upcoming Family Events');
  };

  const handleExportThisMonth = () => {
    downloadICalFile(thisMonthEvents, 'this-month-family-events.ics', 'This Month\'s Family Events');
  };

  const handleExportAll = () => {
    downloadICalFile(allEvents, 'complete-family-calendar.ics', 'Complete Family Calendar');
  };

  // Get events for current month view
  const getMonthEvents = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    return filteredEvents.filter(event => {
      return event.date.getFullYear() === year && event.date.getMonth() === month;
    });
  };

  // Get events for current year view
  const getYearEvents = () => {
    const year = currentDate.getFullYear();
    return filteredEvents.filter(event => event.date.getFullYear() === year);
  };

  // Generate calendar days for month view
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const monthEvents = getMonthEvents();

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const dayEvents = monthEvents.filter(event => 
        event.date.getDate() === date.getDate() &&
        event.date.getMonth() === date.getMonth() &&
        event.date.getFullYear() === date.getFullYear()
      );

      days.push({
        date: new Date(date),
        isCurrentMonth: date.getMonth() === month,
        events: dayEvents
      });
    }

    return days;
  };

  // Render dashboard view (integrated CalendarDashboard functionality)
  const renderDashboardView = () => {
    if (!allEvents || allEvents.length === 0) {
      return (
        <div className="calendar-dashboard">
          <div className="dashboard-loading">
            <p>Loading family calendar data...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="calendar-dashboard container">
        {/* Today's Events */}
        {todayEvents.length > 0 && (
          <div className="dashboard-section today-section">
            <h3>
              <i className="fas fa-star"></i>
              Today's Special Days
            </h3>
            <div className="today-events">
              {todayEvents.map(event => (
                <div key={event.id} className={`today-event ${event.type}`}>
                  <div className="event-icon">{event.icon}</div>
                  <div className="event-content">
                    <h4>{event.person}</h4>
                    <p>{event.description}</p>
                    {event.yearsAgo && (
                      <span className="milestone-badge">
                        {event.yearsAgo}{getOrdinalSuffix(event.yearsAgo)} Anniversary
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Countdown Timers */}
        <div className="dashboard-section countdown-section">
          <h3>
            <i className="fas fa-clock"></i>
            Upcoming Celebrations
          </h3>
          {upcomingEvents.length > 0 ? (
            <div className="countdown-grid">
              {upcomingEvents.slice(0, 6).map(event => {
                const daysUntil = getDaysUntil(event.date);
                const isMilestone = milestoneEvents.some(m => m.id === event.id);
                
                return (
                  <div key={event.id} className={`countdown-card ${event.type} ${isMilestone ? 'milestone' : ''}`}>
                    <div className="countdown-header">
                      <div className="event-icon">{event.icon}</div>
                      <div className="countdown-timer">
                        <div className="countdown-number">{Math.abs(daysUntil)}</div>
                        <div className="countdown-unit">
                          {daysUntil === 0 ? 'Today!' : 
                           daysUntil === 1 ? 'Day' : 'Days'}
                        </div>
                      </div>
                    </div>
                    <div className="countdown-content">
                      <h4>{event.person}</h4>
                      <p className="countdown-description">{event.description}</p>
                      <div className="countdown-date">
                        {formatDisplayDate(event.date, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </div>
                      {isMilestone && (
                        <div className="milestone-indicator">
                          <i className="fas fa-trophy"></i>
                          Milestone!
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-upcoming">
              <p>No upcoming events in the next 30 days.</p>
            </div>
          )}
        </div>

        {/* This Month's Events */}
        {thisMonthEvents.length > 0 && (
          <div className="dashboard-section month-section">
            <h3>
              <i className="fas fa-calendar-alt"></i>
              This Month's Celebrations ({thisMonthEvents.length})
            </h3>
            <div className="month-events">
              {thisMonthEvents.map(event => (
                <div key={event.id} className={`month-event ${event.type}`}>
                  <div className="event-date">
                    <div className="date-number">{new Date(event.date).getDate()}</div>
                    <div className="date-day">
                      {formatDisplayDate(event.date, { weekday: 'short' })}
                    </div>
                  </div>
                  <div className="event-icon">{event.icon}</div>
                  <div className="event-details">
                    <h4>{event.person}</h4>
                    <p>{event.description}</p>
                    {event.yearsAgo && (
                      <span className="years-badge">{event.yearsAgo} years</span>
                    )}
                  </div>
                  <div className="countdown-mini">
                    {formatCountdown(getDaysUntil(event.date))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* On This Day in History */}
        {onThisDayEvents.length > 0 && (
          <div className="dashboard-section history-section">
            <h3>
              <i className="fas fa-history"></i>
              On This Day in Family History
            </h3>
            <div className="history-events">
              {onThisDayEvents.map(event => {
                const yearsAgo = new Date().getFullYear() - new Date(event.date).getFullYear();
                return (
                  <div key={event.id} className={`history-event ${event.type}`}>
                    <div className="history-year">{new Date(event.date).getFullYear()}</div>
                    <div className="event-icon">{event.icon}</div>
                    <div className="history-content">
                      <h4>{event.person}</h4>
                      <p>{event.description}</p>
                      <span className="years-ago">{yearsAgo} years ago</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="dashboard-section stats-section">
            <h3>
              <i className="fas fa-chart-bar"></i>
              Family Calendar Statistics
            </h3>
            <div className="stats-grid">
              <div className="stat-card calendar-stat-card-fix">
                <div className="stat-number">{stats.upcomingCount}</div>
                <div className="stat-label">Upcoming Events</div>
                <div className="stat-sublabel">Next 30 days</div>
              </div>
              <div className="stat-card calendar-stat-card-fix">
                <div className="stat-number">{stats.thisMonthCount}</div>
                <div className="stat-label">This Month</div>
                <div className="stat-sublabel">Celebrations</div>
              </div>
              <div className="stat-card calendar-stat-card-fix">
                <div className="stat-number">{stats.milestonesCount}</div>
                <div className="stat-label">Milestones</div>
                <div className="stat-sublabel">Special anniversaries</div>
              </div>
              <div className="stat-card calendar-stat-card-fix">
                <div className="stat-number">{stats.totalFamilyMembers}</div>
                <div className="stat-label">Family Members</div>
                <div className="stat-sublabel">In our tree</div>
              </div>
              <div className="stat-card calendar-stat-card-fix">
                <div className="stat-number">{stats.yearsOfHistory}+</div>
                <div className="stat-label">Years of History</div>
                <div className="stat-sublabel">Since 1866</div>
              </div>
              <div className="stat-card calendar-stat-card-fix">
                <div className="stat-number">{stats.totalEvents}</div>
                <div className="stat-label">Total Events</div>
                <div className="stat-sublabel">All celebrations</div>
              </div>
            </div>
          </div>

        {/* Export Controls */}
        <div className="dashboard-section export-section">
            <h3>
              <i className="fas fa-download"></i>
              Export & Share Calendar
            </h3>
            <p>Export family events to your personal calendar or share with others</p>
            
            <div className="export-options">
              <div className="export-option-group">
                <h4>Upcoming Events ({upcomingEvents.length})</h4>
                <ExportShareControls 
                  data={upcomingEvents}
                  contentType="calendar"
                  title="Upcoming Family Events"
                  showBackupControls={false}
                  className="calendar-export-upcoming"
                />
              </div>
              
              <div className="export-option-group">
                <h4>This Month ({thisMonthEvents.length})</h4>
                <ExportShareControls 
                  data={thisMonthEvents}
                  contentType="calendar"
                  title="This Month's Family Events"
                  showBackupControls={false}
                  className="calendar-export-month"
                />
              </div>
              
              <div className="export-option-group">
                <h4>All Events ({allEvents.length})</h4>
                <ExportShareControls 
                  data={allEvents}
                  contentType="calendar"
                  title="Complete Family Calendar"
                  showBackupControls={true}
                  className="calendar-export-all"
                />
              </div>
            </div>
            
            <div className="export-help">
              <p><strong>How to use:</strong> Click Export to download calendar files, then open with your calendar app (Google Calendar, Apple Calendar, Outlook, etc.) to add the events.</p>
            </div>
          </div>
      </div>
    );
  };

  // Render month view
  const renderMonthView = () => {
    const days = generateCalendarDays();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return (
      <div className="calendar-month-view">
        <div className="calendar-header">
          <button onClick={() => navigateMonth(-1)} className="nav-btn">
            <i className="fas fa-chevron-left"></i>
          </button>
          <h3>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
          <button onClick={() => navigateMonth(1)} className="nav-btn">
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        <div className="calendar-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>

        <div className="calendar-days">
          {days.map((day, index) => (
            <div
              key={index}
              className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${
                day.events.length > 0 ? 'has-events' : ''
              }`}
            >
              <div className="day-number">{day.date.getDate()}</div>
              {day.events.length > 0 && (
                <div className="day-events">
                  {day.events.slice(0, 3).map(event => (
                    <div key={event.id} className={`event-dot ${event.type}`} title={event.description}>
                      {event.icon}
                    </div>
                  ))}
                  {day.events.length > 3 && (
                    <div className="more-events">+{day.events.length - 3}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render year view
  const renderYearView = () => {
    const yearEvents = getYearEvents();
    const eventsByMonth = {};

    yearEvents.forEach(event => {
      const month = event.date.getMonth();
      if (!eventsByMonth[month]) eventsByMonth[month] = [];
      eventsByMonth[month].push(event);
    });

    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return (
      <div className="calendar-year-view">
        <div className="calendar-header">
          <button onClick={() => navigateYear(-1)} className="nav-btn">
            <i className="fas fa-chevron-left"></i>
          </button>
          <h3>{currentDate.getFullYear()}</h3>
          <button onClick={() => navigateYear(1)} className="nav-btn">
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        <div className="year-months">
          {monthNames.map((month, index) => (
            <div
              key={month}
              className={`year-month ${eventsByMonth[index] ? 'has-events' : ''}`}
              onClick={() => {
                const newDate = new Date(currentDate);
                newDate.setMonth(index);
                setCurrentDate(newDate);
                setViewMode('month');
              }}
            >
              <div className="month-name">{month}</div>
              <div className="month-event-count">
                {eventsByMonth[index] ? eventsByMonth[index].length : 0} events
              </div>
              {eventsByMonth[index] && (
                <div className="month-events-preview">
                  {eventsByMonth[index].slice(0, 3).map(event => (
                    <div key={event.id} className={`event-preview ${event.type}`}>
                      {event.icon} {event.person}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render timeline view
  const renderTimelineView = () => {
    const timelineEvents = filteredEvents.slice(0, 50); // Limit for performance

    return (
      <div className="calendar-timeline-view">
        <div className="timeline-header">
          <h3>Family Timeline</h3>
          <p>{filteredEvents.length} total events</p>
        </div>

        <div className="timeline-events">
          {timelineEvents.map(event => (
            <div key={event.id} className={`timeline-event ${event.type}`}>
              <div className="event-date">
                <div className="event-icon">{event.icon}</div>
                <div className="date-info">
                  <div className="event-day">{event.date.getDate()}</div>
                  <div className="event-month">
                    {event.date.toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                  <div className="event-year">{event.date.getFullYear()}</div>
                </div>
              </div>
              <div className="event-details">
                <div className="event-description">{event.description}</div>
                {event.location && (
                  <div className="event-location">
                    <i className="fas fa-map-marker-alt"></i> {event.location}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container-wrapper max-width-default padding-default">
      <div className="page-header">
        <h1>
          <i className="fas fa-calendar-alt"></i>
          Family Calendar
        </h1>
        <p>
          Explore our family's important dates and milestones. View upcoming celebrations, 
          browse historical events, and discover family history through multiple calendar views.
        </p>
      </div>
      
      <div className="page-content">

        {/* Loading and Error States */}
        {isLoading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading family calendar data...</p>
          </div>
        )}
        
        {error && (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p>Error loading family data: {error}</p>
            <div className="error-message">
              <p>Unable to load family data. Please check the console for details.</p>
            </div>
          </div>
        )}

        {/* Calendar Controls - Always visible when data is loaded */}
        {gedcomData && !isLoading && !error && (
          <div className="calendar-controls">
            <div className="calendar-filters">
              <div className="view-modes">
                <button
                  className={`mode-btn ${viewMode === 'dashboard' ? 'active' : ''}`}
                  onClick={() => setViewMode('dashboard')}
                >
                  <i className="fas fa-tachometer-alt"></i>
                  Dashboard
                </button>
                <button
                  className={`mode-btn ${viewMode === 'month' ? 'active' : ''}`}
                  onClick={() => setViewMode('month')}
                >
                  <i className="fas fa-calendar"></i>
                  Month
                </button>
                <button
                  className={`mode-btn ${viewMode === 'year' ? 'active' : ''}`}
                  onClick={() => setViewMode('year')}
                >
                  <i className="fas fa-calendar-alt"></i>
                  Year
                </button>
                <button
                  className={`mode-btn ${viewMode === 'timeline' ? 'active' : ''}`}
                  onClick={() => setViewMode('timeline')}
                >
                  <i className="fas fa-list"></i>
                  Timeline
                </button>
              </div>

              <div className="event-filters">
                <select
                  value={selectedEventType}
                  onChange={(e) => setSelectedEventType(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Events ({allEvents.length})</option>
                  <option value="anniversaries">This Year's Anniversaries ({anniversaries.length})</option>
                  <option value="historical">Historical Events ({originalEvents.length})</option>
                  <option value="birth">Births & Birthdays ({allEvents.filter(e => e.type === 'birth' || e.type === 'birth-anniversary').length})</option>
                  <option value="death">Deaths & Memorials ({allEvents.filter(e => e.type === 'death' || e.type === 'death-anniversary').length})</option>
                  <option value="marriage">Marriages & Anniversaries ({allEvents.filter(e => e.type === 'marriage' || e.type === 'marriage-anniversary').length})</option>
                </select>
              </div>

              {(viewMode === 'month' || viewMode === 'year') && (
                <button className="today-btn" onClick={goToToday}>
                  <i className="fas fa-calendar-day"></i>
                  Today
                </button>
              )}
            </div>
          </div>
        )}

        {/* Calendar Content - All views integrated on same page */}
        {gedcomData && !isLoading && !error && (
          <div className="calendar-content">
            {viewMode === 'dashboard' && renderDashboardView()}
            {viewMode === 'month' && renderMonthView()}
            {viewMode === 'year' && renderYearView()}
            {viewMode === 'timeline' && renderTimelineView()}
          </div>
        )}

        {/* Legend - Show for all views except dashboard (which has its own) */}
        {gedcomData && !isLoading && !error && viewMode !== 'dashboard' && (
          <div className="calendar-legend">
            <div className="legend-items">
              <div className="legend-item">
                <span className="legend-icon birth">🎂</span>
                <span>Births</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon death">🕊️</span>
                <span>Deaths</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon marriage">💒</span>
                <span>Marriages</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon birth-anniversary">🎉</span>
                <span>Birthdays</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon death-anniversary">🌹</span>
                <span>Memorials</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon marriage-anniversary">💕</span>
                <span>Wedding Anniversaries</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
